import React from "react";
import { useNavigate } from "react-router-dom";
import PortfolioNavbar from "./PortfolioNavbar";
import PortfolioFooter from "./PortfolioFooter";
import PageHero from "./PageHero";
import { Helmet } from "react-helmet-async";
import {
  Zap,
  Users,
  Cpu,
  Target,
  Briefcase,
  FileText,
  Layers,
  Code,
  CheckCircle,
  Smartphone,
  Server,
  Database,
  Cloud,
  Shield,
  Activity,
  Building2,
  ArrowRight,
} from "lucide-react";

const OurCoreWorks = () => {
  const navigate = useNavigate();

  // Helper component for the hexagon icon (reused style)
  const HexagonIcon = ({ children, color }) => (
    <div
      className={`w-14 h-14 flex items-center justify-center p-1 rounded-lg shadow-lg`}
      style={{
        clipPath:
          "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
        background: color,
        transform: "rotate(0deg)",
      }}
    >
      {children}
    </div>
  );

  // Helper component for all list items
  const ListItem = ({ children, icon, color = "text-orange-600" }) => (
    <div className="flex items-start">
      <div
        className="flex-shrink-0 p-1 mr-3 rounded-lg h-6 w-6 flex items-center justify-center"
        style={{ backgroundColor: "#FFEDD5" }}
      >
        {React.cloneElement(icon, { className: `w-4 h-4 ${color}` })}
      </div>
      <p className="text-gray-700 text-base">{children}</p>
    </div>
  );

  // --- Content Data for Software Solutions ---

  // 1. Core Engineering Expertise
  const coreExpertise = [
    {
      icon: <Users className="w-6 h-6 text-orange-600" />,
      title: "Engineering Team",
      detail:
        "A dedicated team of **50+ full-stack engineers** ensuring high-quality software delivery.",
    },
    {
      icon: <Cloud className="w-6 h-6 text-orange-600" />,
      title: "Cloud Infrastructure",
      detail:
        "Managing robust cloud setups with **99.9% availability** on AWS, Azure, and Google Cloud.",
    },
    {
      icon: <Zap className="w-6 h-6 text-orange-600" />,
      title: "Agile Velocity",
      detail:
        "Expert in rapid MVP development and deployment, ensuring **fast time-to-market** for core features.",
    },
    {
      icon: <Shield className="w-6 h-6 text-orange-600" />,
      title: "Technical Reliability",
      detail:
        "**Consistently delivering robust applications** designed for high-load and sensitive data environments.",
    },
  ];

  const reporting = [
    {
      title: "Uptime Monitoring",
      detail: "Real-time system health tracking with sub-second resolution.",
      icon: <Activity className="w-5 h-5" />,
    },
    {
      title: "Tech Audits",
      detail: "Comprehensive code quality and security audit reports delivered weekly.",
      icon: <FileText className="w-5 h-5" />,
    },
  ];

  // 2. Specialized Technical Services
  const specializedServices = [
    {
      title: "System Architecture",
      detail: "Experts in designing distributed systems and microservices.",
      highlight: "Scalable architecture supporting millions of concurrent users.",
      icon: <Cpu className="w-5 h-5" />,
    },
    {
      title: "Cyber Security",
      detail:
        "Specializing in penetration testing and zero-trust security implementations.",
      icon: <Shield className="w-5 h-5" />,
    },
  ];

  // 3. Full-Stack Capabilities
  const technicalCapabilities = [
    {
      text: "Enterprise Web Applications (React, Next.js, Angular)",
      icon: <Code />,
    },
    {
      text: "Scalable Backend Services (Node.js, Python, Go)",
      icon: <Server />,
    },
    { text: "Native & Cross-Platform Mobile Apps (iOS, Android, Flutter)", icon: <Smartphone /> },
    { text: "Database Management & Optimization (PostgreSQL, MongoDB, Redis)", icon: <Database /> },
    { text: "DevOps & CI/CD Pipeline Automation (GitHub Actions, Jenkins)", icon: <Zap /> },
    { text: "Cloud Migration & Managed Services (AWS, Azure, GCP)", icon: <Cloud /> },
    { text: "UI/UX Design & Prototyping (Figma, Adobe XD)", icon: <Layers /> },
    {
      text: "Legacy System Modernization & API Integration",
      icon: <Briefcase />,
    },
    { text: "Technical Consultation & Product Roadmap Strategy", icon: <Target /> },
  ];

  // 4. Enterprise, SaaS, and Case Studies
  const solutionsAndCaseStudies = [
    {
      title: "Enterprise Solutions & ERP",
      points: [
        {
          text: "Developed custom ERP systems for large-scale manufacturing operations",
          icon: <CheckCircle />,
        },
        {
          text: "Integrated real-time inventory and supply chain tracking modules",
          icon: <CheckCircle />,
        },
        {
          text: "Implemented role-based access control and high-security data storage",
          icon: <CheckCircle />,
        },
        {
          text: "Automated business workflows reducing operational costs by 30%",
          icon: <CheckCircle />,
        },
      ],
      icon: <Building2 className="w-6 h-6 text-white" />,
      color: "linear-gradient(45deg, #1e3a8a, #3b82f6)", 
    },
    {
      title: "SaaS & Product Engineering",
      points: [
        {
          text: "Built multi-tenant SaaS platforms with scalable subscription models",
          icon: <CheckCircle />,
        },
        {
          text: "Integrated seamless payment gateways (Stripe, Razorpay, PayPal)",
          icon: <CheckCircle />,
        },
        {
          text: "Designed high-performance analytics dashboards with real-time data",
          icon: <CheckCircle />,
        },
        {
          text: "Optimized frontend performance for sub-second page load times",
          icon: <CheckCircle />,
        },
        {
          text: "Implemented automated QA suites ensuring zero-regression releases",
          icon: <CheckCircle />,
        },
      ],
      icon: <Zap className="w-6 h-6 text-white" />,
      color: "linear-gradient(45deg, #ea580c, #f97316)",
    },
  ];

  const softwareProjects = [
    {
      title: "Fintech Wallet – Mobile & Web Platform",
      details: [
        "Architected secure transaction processing system",
        "Implemented biometrics and multi-factor authentication",
        "Achieved sub-200ms processing time for cross-border payments",
      ],
      platformIcon: <Smartphone className="w-6 h-6 text-white" />,
      platformColor: "linear-gradient(45deg, #059669, #10b981)",
    },
    {
      title: "Healthcare ERP – Multi-Region Deployment",
      details: [
        "Centralized patient record management across 20+ locations",
        "Compliance with HIPAA and local data protection regulations",
        "Real-time resource allocation and automated billing system",
      ],
      platformIcon: <Database className="w-6 h-6 text-white" />,
      platformColor: "linear-gradient(45deg, #2563eb, #3b82f6)",
    },
    {
      title: "Real-time Analytics – Cloud-Native Dashboard",
      details: [
        "Processing 1TB+ data daily with optimized data pipelines",
        "Serverless architecture reducing infrastructure costs by 40%",
        "High-availability deployment across 3 AWS regions",
      ],
      platformIcon: <Cloud className="w-6 h-6 text-white" />,
      platformColor: "linear-gradient(135deg, #7c3aed, #8b5cf6)",
    },
    {
      title: "E-commerce Engine – Scalable Architecture",
      details: [
        "Supports 100k+ concurrent users during peak flash sales",
        "Integrated AI-driven recommendation and search system",
        "99.99% availability during high-traffic promotional events",
      ],
      platformIcon: <Zap className="w-6 h-6 text-white" />,
      platformColor: "linear-gradient(45deg, #dc2626, #ef4444)",
    },
  ];

  return (
    <div className="relative min-h-screen bg-gray-50 overflow-hidden font-sans">
      <Helmet>
        <title>Core Solutions | Winsil Evolution Pvt Ltd</title>
        <meta name="description" content="Discover our core engineering expertise, from enterprise ERP systems to scalable SaaS platforms and cloud-native dashboards." />
        <meta property="og:title" content="Core Solutions | Winsil Evolution Pvt Ltd" />
        <meta property="og:description" content="Full-stack technical capabilities including React, Node.js, AWS, and mobile app development." />
      </Helmet>
      <PortfolioNavbar />

      <PageHero
        label="Core Solutions"
        labelClass="text-orange-400"
        title="Core Solutions & Project Portfolios"
        subtitle="A comprehensive look at our engineering capabilities, specialized services, and the high-impact software projects we have delivered for global clients."
        image="/images/photo-1460925895917-afdab827c52f.jpg"
        imageAlt="Analytics dashboard on laptop screen"
        bgClass="bg-gray-950"
        glowColor="#f97316"
      />

      {/* ── Image Gallery ── */}
      <div className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-center mb-2 text-orange-500">Portfolio Highlights</p>
          <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-10">Core Solutions in Action</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { src: "/images/photo-1555066931-4365d14bab8c.jpg", alt: "Custom software development", label: "Custom Software" },
              { src: "/images/photo-1512941937669-90a1b58e7e9c.jpg", alt: "Mobile app development", label: "Mobile Apps" },
              { src: "/images/photo-1451187580459-43490279c0fa.jpg", alt: "Cloud infrastructure", label: "Cloud Architecture" },
            ].map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-64">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            {[
              { src: "/images/photo-1460925895917-afdab827c52f.jpg", alt: "Enterprise ERP analytics", label: "Enterprise ERP" },
              { src: "/images/photo-1522202176988-66273c2fd55f.jpg", alt: "SaaS platform team", label: "SaaS Platforms" },
            ].map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-56">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Stats Strip ── */}
      <div className="bg-gray-900 border-y border-white/10 py-12 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            { value: "100+", label: "Projects Delivered" },
            { value: "50+",  label: "Engineers" },
            { value: "99.9%", label: "Uptime SLA" },
            { value: "6+",   label: "Years Experience" },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-3xl sm:text-4xl font-black text-orange-400 mb-1">{s.value}</p>
              <p className="text-xs font-bold uppercase tracking-widest text-white/40">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto pt-12 pb-12 px-4 sm:px-6 lg:px-8 z-10">

        {/* --- SECTION 1: CORE CAPABILITIES --- */}
        <div className="bg-white p-8 rounded-xl shadow-xl mb-16 border-t-4 border-orange-500">
          <div className="flex items-center space-x-4 mb-8 border-b pb-4">
            <HexagonIcon color="linear-gradient(45deg, #e34040, #ff9900)">
              <Cpu className="w-6 h-6 text-white" />
            </HexagonIcon>
            <h2 className="text-3xl font-bold text-orange-600">
              Our Core Engineering Expertise
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-10">
            {coreExpertise.map((item, index) => (
              <div
                key={index}
                className="p-4 bg-orange-50 rounded-lg shadow-md border-b-4 border-orange-500"
              >
                <div className="flex items-center mb-3">
                  <div className="p-2 bg-orange-100 rounded-lg mr-3">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-bold uppercase tracking-wider text-orange-600">
                    {item.title.split(" ")[0]}
                  </h3>
                </div>
                <p className="text-gray-700 text-sm">
                  <span
                    dangerouslySetInnerHTML={{
                      __html: item.detail.replace(
                        /\*\*(.*?)\*\*/g,
                        "<strong>$1</strong>"
                      ),
                    }}
                  />
                </p>
              </div>
            ))}
          </div>

          <div className="border-t pt-6 mt-6">
            <h3 className="text-xl font-bold text-orange-600 mb-4">
              Real-time Monitoring & Reporting
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {reporting.map((item, index) => (
                <ListItem key={index} icon={item.icon}>
                  <span className="font-bold">{item.title}:</span>{" "}
                  <span className="text-lg font-semibold text-gray-900">
                    {item.detail}
                  </span>
                </ListItem>
              ))}
            </div>
          </div>
        </div>

        {/* --- SECTION 2: SPECIALIZED SERVICES --- */}
        <div className="bg-orange-50 p-8 rounded-xl shadow-inner mb-16 border-t-4 border-orange-600">
          <div className="flex items-center space-x-4 mb-8 border-b border-orange-100 pb-4">
            <HexagonIcon color="linear-gradient(45deg, #8A3AB9, #E95950)">
              <Zap className="w-6 h-6 text-white" />
            </HexagonIcon>
            <h2 className="text-3xl font-bold text-orange-600">
              Specialized Engineering Services
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-orange-600 mb-4">
                Specialized Services
              </h3>
              {specializedServices.map((service, index) => (
                <div
                  key={index}
                  className="space-y-1 p-4 border-l-4 border-orange-500 bg-white shadow-md rounded-lg"
                >
                  <div className="flex items-center space-x-2 mb-1">
                    <Zap className="w-5 h-5 text-orange-600 flex-shrink-0" />
                    <h4 className="font-extrabold text-gray-900 text-lg">
                      {service.title}
                    </h4>
                  </div>
                  <p className="text-gray-700 text-sm">{service.detail}</p>
                  {service.highlight && (
                    <p className="text-sm italic text-orange-600 font-semibold mt-1">
                      Key Strength: {service.highlight}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="pt-6 lg:pt-0">
              <h3 className="text-2xl font-bold text-orange-600 mb-4">
                Full-Stack Technical Capabilities
              </h3>
              <p className="font-bold text-gray-800 mb-4">
                Comprehensive engineering support: Building high-quality, tailored solutions
                across the entire <span className="text-orange-600">software ecosystem</span>.
              </p>
              <h4 className="font-semibold text-gray-800 mb-2">
                Our Core Expertise:
              </h4>
              <ul className="space-y-2 text-gray-700 text-base">
                {technicalCapabilities.map((skill, index) => (
                  <ListItem key={index} icon={skill.icon} color="text-orange-500">
                    {skill.text}
                  </ListItem>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* --- SECTION 3: SOLUTIONS (Horizontal) --- */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
          {solutionsAndCaseStudies.map((service, index) => (
            <div
              key={index}
              className="bg-white p-8 rounded-xl shadow-xl border-t-4 border-orange-500"
            >
              <div className="flex items-center space-x-4 mb-6">
                <HexagonIcon color={service.color}>{service.icon}</HexagonIcon>
                <h2 className="text-2xl font-bold text-orange-600">
                  {service.title}
                </h2>
              </div>
              <ul className="space-y-3 text-gray-700">
                {service.points.map((point, pIndex) => (
                  <ListItem key={pIndex} icon={point.icon} color="text-orange-500">
                    {point.text}
                  </ListItem>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* --- SECTION 4: SOFTWARE PROJECT CASE STUDIES --- */}
        <div className="bg-white p-8 rounded-xl shadow-xl border-t-4 border-orange-500">
          <h2 className="text-4xl font-extrabold text-orange-600 mb-8 border-b pb-4">
            Technical Case Studies
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {softwareProjects.map((project, index) => (
              <div
                key={index}
                className="p-6 border-l-4 border-orange-500 bg-gray-50 rounded shadow-md"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <HexagonIcon color={project.platformColor}>
                    {project.platformIcon}
                  </HexagonIcon>
                  <h3 className="text-xl font-bold text-gray-900">
                    {project.title}
                  </h3>
                </div>
                <ul className="space-y-3 text-gray-700 text-base">
                  {project.details.map((detail, dIndex) => (
                    <ListItem
                      key={dIndex}
                      icon={<CheckCircle />}
                      color="text-orange-600"
                    >
                      {detail}
                    </ListItem>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Why Winsil ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-orange-500 mb-3">Why Winsil</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6">
              Engineering That <span className="text-orange-500">Delivers Results</span>
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-8">
              We don't just write code — we engineer solutions. Every project is built with a focus on
              performance, security, and long-term maintainability, backed by a team that takes ownership.
            </p>
            <ul className="space-y-3 mb-10">
              {[
                "Dedicated engineering team for every project",
                "Agile delivery with weekly demos and transparency",
                "Code reviews and quality gates on every PR",
                "Post-launch support and performance monitoring",
                "Full IP ownership transferred on completion",
              ].map((item) => (
                <li key={item} className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <span className="text-gray-700 text-sm font-medium">{item}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-black uppercase tracking-wider rounded-lg bg-orange-500 text-white hover:bg-orange-400 transition-all duration-300 hover:scale-105 shadow-lg shadow-orange-500/25"
            >
              Start a Project <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
            <img
              src="/images/photo-1552664730-d307ca884978.jpg"
              alt="Engineering team at work"
              className="w-full h-[420px] object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-gray-900/60 via-transparent to-orange-500/10" />
            <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3 shadow-xl">
              <p className="text-2xl font-black text-orange-600">100+</p>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Projects Delivered</p>
            </div>
            <div className="absolute top-5 right-5 bg-orange-500/90 text-white rounded-full px-4 py-1.5 text-xs font-black tracking-wider shadow-lg">
              Full-Stack Team
            </div>
          </div>
        </div>
      </div>

      {/* ── CTA Banner ── */}
      <div className="relative bg-gray-950 py-20 px-4 overflow-hidden text-white text-center">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 to-amber-400" />
        <div className="relative max-w-3xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-orange-400 mb-4">Ready to Build?</p>
          <h2 className="text-4xl sm:text-5xl font-black mb-6">Let's Engineer Your Next Product</h2>
          <p className="text-white/55 text-lg mb-10 max-w-xl mx-auto">From MVP to enterprise-scale platform — tell us what you're building and we'll make it happen.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center justify-center px-8 py-4 text-sm font-black uppercase tracking-wider rounded-lg bg-orange-500 text-white hover:bg-orange-400 transition-all duration-300 hover:scale-105 shadow-lg"
            >
              Get a Free Consultation <ArrowRight className="w-4 h-4 ml-2" />
            </button>
            <button
              onClick={() => navigate("/case-studies")}
              className="inline-flex items-center justify-center px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-lg border-2 border-white/20 text-white/70 hover:border-orange-400 hover:text-white transition-all duration-300"
            >
              View Case Studies
            </button>
          </div>
        </div>
      </div>

      <PortfolioFooter />
    </div>
  );
};

export default OurCoreWorks;
